/**
 * Modificadores ÚNICOS — categoría de modifiers de equipamiento que:
 *   - viven principalmente en items `unique: true` (rareza LEGENDARY),
 *   - tienen valor FIJO (flag `1`), nunca se rolean,
 *   - NO están en el pool de afijos (`shared/data/affixes.ts`), por lo que jamás
 *     salen en drops RARE/EPIC ni tienen tiers.
 *
 * (Excepción: `noWalkNoise` también aparece en un enchant y en el buff de
 * invisibilidad — se registra igual acá por su displayName/aviso de consola.)
 *
 * El sistema de items ya soporta mods fijos por template (`Item.modifiers`); este
 * registro sólo formaliza la categoría para:
 *   - el display en el tooltip del inventario (nombre/descr en vez de "+1 key"),
 *   - validaciones/documentación de qué keys son "únicas".
 *
 * Para AGREGAR un mod único nuevo:
 *   1. Sumar la key flag a `ItemModifiers` (`shared/types/item.ts`).
 *   2. Sumar una entrada acá con displayName/description.
 *   3. NO agregarlo al `AFFIX_POOL` (así no rolea).
 *   4. Cablear su efecto donde corresponda (server/cliente).
 */
import type { ItemModifiers } from '../types/item';

export interface UniqueModifierDef {
  /** Key dentro de `ItemModifiers`. */
  key: keyof ItemModifiers;
  /** Nombre para el tooltip (español). */
  displayName: string;
  /** Qué hace, para el tooltip (español). */
  description: string;
}

export const UNIQUE_MODIFIERS: readonly UniqueModifierDef[] = [
  {
    key: 'seeDeadNames',
    displayName: 'Visión espectral',
    description: 'Revela el nombre de las almas (jugadores muertos).',
  },
  {
    key: 'talkToDead',
    displayName: 'Voz del más allá',
    description: 'Te permite leer y hablar con los muertos.',
  },
  {
    key: 'alwaysCrit',
    displayName: 'Golpe mortal',
    description: 'Todos tus golpes cuerpo a cuerpo son críticos.',
  },
  {
    key: 'selfSilence',
    displayName: 'Voto de silencio',
    description: 'Mientras lo tengas equipado no podés lanzar hechizos.',
  },
  {
    key: 'auraSingularity',
    displayName: 'Aura suprema',
    description: 'Solo podés tener un aura activa a la vez, pero su efecto se duplica.',
  },
  {
    key: 'immobilizeEveryHit',
    displayName: 'Raíces eternas',
    description: 'Cada golpe cuerpo a cuerpo inmoviliza al objetivo con raíces.',
  },
  {
    key: 'impairImmunity',
    displayName: 'Voluntad inquebrantable',
    description: 'No podés ser inmovilizado ni paralizado: nunca perdés tu movilidad.',
  },
  {
    key: 'seeInvisible',
    displayName: 'Ojo revelador',
    description: 'Ves a los jugadores invisibles: sus siluetas y nombres se te revelan.',
  },
  {
    key: 'invisibilityNeverExpires',
    displayName: 'Velo eterno',
    description: 'Tu invisibilidad no se desvanece con el tiempo: permanece hasta que la rompas.',
  },
  {
    key: 'silentWhileInvisible',
    displayName: 'Paso espectral',
    description: 'Mientras estés invisible no dejás huellas ni hacés ruido al caminar.',
  },
  {
    key: 'unboundSpellRange',
    displayName: 'Conjuro sin fronteras',
    description: 'Tus hechizos no tienen distancia mínima ni máxima: los lanzás desde donde sea.',
  },
  {
    key: 'summonAggressiveMode',
    displayName: 'Instinto asesino',
    description:
      'Tus invocaciones pueden entrar en modo agresivo: atacan a cualquier enemigo que se cruce.',
  },
  {
    key: 'summonFrenzy',
    displayName: 'Frenesí de la manada',
    description:
      'Tus invocaciones atacan mucho más rápido, pero pierden por completo la capacidad de dar críticos.',
  },
  {
    key: 'summonExecutioner',
    displayName: 'Verdugo paciente',
    description:
      'Tus invocaciones atacan muy lento, pero cada golpe descarga un daño devastador.',
  },
  {
    key: 'summonPackLeader',
    displayName: 'Líder de la manada',
    description: 'Cada invocación trae una criatura extra.',
  },
  {
    key: 'summonLimitFixed',
    displayName: 'Vínculo múltiple',
    description: 'Podés mantener hasta 5 invocaciones a la vez.',
  },
  {
    key: 'blockOmnidirectional',
    displayName: 'Guardia total',
    description: 'Podés bloquear golpes que vengan de cualquier dirección, no sólo de frente.',
  },
  {
    key: 'knockbackOnHitTiles',
    displayName: 'Golpe demoledor',
    description: 'Cada golpe cuerpo a cuerpo empuja al objetivo hacia atrás.',
  },
  {
    key: 'noFootsteps',
    displayName: 'Paso fantasma',
    description: 'No dejás huellas en el suelo al caminar: te movés sin rastro.',
  },
  {
    key: 'noWalkNoise',
    displayName: 'Andar silencioso',
    description: 'No hacés ruido al caminar (audio pendiente — ver docs/SOUND_AND_MUSIC.md).',
  },
  {
    key: 'partyXpControl',
    displayName: 'Pacto del líder',
    description:
      'Podés repartir a mano la experiencia del party: fijá el % de cada miembro (máx. 75% cada uno).',
  },
] as const;

/** Set de keys reconocidas como modificadores únicos (lookup O(1)). */
export const UNIQUE_MODIFIER_KEYS: ReadonlySet<keyof ItemModifiers> = new Set(
  UNIQUE_MODIFIERS.map((m) => m.key)
);

/** True si la key es un modificador único (no rolea, sin tiers, sólo en `unique`). */
export function isUniqueModifierKey(key: string): boolean {
  return UNIQUE_MODIFIER_KEYS.has(key as keyof ItemModifiers);
}

/** Busca la def de un mod único por key. */
export function getUniqueModifier(key: string): UniqueModifierDef | undefined {
  return UNIQUE_MODIFIERS.find((m) => m.key === key);
}
